/**
 * Fetch Adaptive UX flags from backend. Cache in memory.
 * If UX_TRACKING_ENABLED is false, tracking must not run.
 */

export interface UxFlags {
  adaptiveUxEnabled: boolean;
  uxTrackingEnabled: boolean;
  uxCultureEnabled: boolean;
  uxCtaEnabled: boolean;
  uxReorderEnabled: boolean;
  uxServicesEnabled: boolean;
  uxFaqEnabled: boolean;
  uxPolicyEnabled: boolean;
  uxPredictEnabled: boolean;
  uxNudgesEnabled: boolean;
  /** Geo Adaptive Layer: country-aware headline/banner/currency. */
  uxGeoEnabled: boolean;
  uxInterventionEnabled: boolean;
  uxDebugOverlayEnabled: boolean;
}

const DEFAULTS: UxFlags = {
  adaptiveUxEnabled: false,
  uxTrackingEnabled: false,
  uxCultureEnabled: false,
  uxCtaEnabled: false,
  uxReorderEnabled: false,
  uxServicesEnabled: false,
  uxFaqEnabled: false,
  uxPolicyEnabled: false,
  uxPredictEnabled: false,
  uxNudgesEnabled: false,
  uxGeoEnabled: false,
  uxInterventionEnabled: false,
  uxDebugOverlayEnabled: false,
};

let cached: UxFlags | null = null;
let fetchPromise: Promise<UxFlags> | null = null;

export async function getUxFlags(): Promise<UxFlags> {
  if (cached !== null) return cached;
  if (fetchPromise !== null) return fetchPromise;
  fetchPromise = (async (): Promise<UxFlags> => {
    try {
      const r = await fetch("/api/ux-flags", { credentials: "same-origin" });
      if (!r.ok) return DEFAULTS;
      const data = (await r.json()) as UxFlags;
      cached = {
        adaptiveUxEnabled: Boolean(data.adaptiveUxEnabled),
        uxTrackingEnabled: Boolean(data.uxTrackingEnabled),
        uxCultureEnabled: Boolean(data.uxCultureEnabled),
        uxCtaEnabled: Boolean(data.uxCtaEnabled),
        uxReorderEnabled: Boolean(data.uxReorderEnabled),
        uxServicesEnabled: Boolean(data.uxServicesEnabled),
        uxFaqEnabled: Boolean(data.uxFaqEnabled),
        uxPolicyEnabled: Boolean(data.uxPolicyEnabled),
        uxPredictEnabled: Boolean(data.uxPredictEnabled),
        uxNudgesEnabled: Boolean(data.uxNudgesEnabled),
        uxGeoEnabled: Boolean(data.uxGeoEnabled),
        uxInterventionEnabled: Boolean(data.uxInterventionEnabled),
        uxDebugOverlayEnabled: Boolean(data.uxDebugOverlayEnabled),
      };
      return cached;
    } catch {
      return DEFAULTS;
    }
  })();
  return fetchPromise;
}
